import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Card, Badge, Nav, Tab, Modal, InputGroup, Dropdown } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faComment, 
  faClock, 
  faPhoneSlash,
  faPaperPlane,
  faEllipsisV,
  faVideo,
  faMicrophone,
  faPlay,
  faCheck,
  faCheckDouble,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useForm, SubmitHandler } from 'react-hook-form';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { dateFormatter } from 'helpers/utils';

// Types
interface CallRecord {
  id: string;
  type: 'outbound' | 'inbound' | 'missed';
  duration?: number;
  timestamp: string;
  status: 'completed' | 'failed' | 'busy' | 'no-answer';
  recording_url?: string;
}

interface SMSMessage {
  id: string;
  direction: 'sent' | 'received';
  message: string;
  timestamp: string;
  status: 'sent' | 'delivered' | 'read' | 'failed' | 'sending';
  media_url?: string;
}

interface CallNote {
  id: string;
  call_note: string;
  created_on: string;
}

type MessageInputs = {
  message: string;
};

const LeadDetailsCommunication = ({ phoneNotes, leadDetails }: any) => {
  const [activeTab, setActiveTab] = useState<'messages' | 'calls' | 'notes'>('messages');
  const [messages, setMessages] = useState<SMSMessage[]>([]);
  const [callHistory, setCallHistory] = useState<CallRecord[]>([]);
  const [callNotes, setCallNotes] = useState<CallNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [showCallModal, setShowCallModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<MessageInputs>();

  const {
    register: registerNote,
    handleSubmit: handleSubmitNote,
    reset: resetNote,
    formState: { errors: noteErrors },
  } = useForm<{ note: string }>();

  const loadCommunicationHistory = React.useCallback(async () => {
    try {
      setIsLoading(true);
      console.log(`Loading communication history for lead ID: ${id}`);
      
      // TODO: Backend endpoint not implemented yet - using fallback
      console.log('⚠️ Conversation history endpoint not implemented yet, using empty state');
      
      // Start with empty messages until endpoint is implemented
      setMessages([]);
      console.log('Using empty conversation history until backend endpoint is ready');
      
      // When endpoint is implemented, uncomment this section:
      /*
      try {
        const response = await axiosInstance.get(`/api/lead/${id}/conversation-history`);
        console.log('Communication history response:', response.data);
        
        if (response.data?.success && response.data?.data) {
          const { messages, conversation } = response.data.data;
          
          if (messages && messages.length > 0) {
            // Convert backend messages to frontend format
            const formattedMessages: SMSMessage[] = messages.map((msg: any) => ({
              id: msg.id,
              direction: msg.role === 'user' ? 'sent' : 'received',
              message: msg.content,
              timestamp: msg.timestamp,
              status: 'delivered'
            }));
            
            setMessages(formattedMessages);
            console.log(`Loaded ${formattedMessages.length} messages`);
          } else {
            // No messages found - start with empty array
            setMessages([]);
            console.log('No messages found in conversation history');
          }
          
          // Store conversation ID if available
          if (conversation) {
            localStorage.setItem(`conversationId_${id}`, conversation.id);
            console.log('Stored conversation ID:', conversation.id);
          }
        } else {
          // No conversation history found - start with empty messages
          setMessages([]);
          console.log('No conversation data in response');
        }
      } catch (error) {
        setMessages([]);
        console.log('Error loading conversation history:', error);
      }
      */

      // Load real call history from backend
      try {
        const callHistoryResponse = await axiosInstance.get(`/api/communications/call-history/${id}`);
        setCallHistory(callHistoryResponse.data.calls || []);
      } catch (error) {
        console.error('Failed to load call history:', error);
        
        // Fall back to mock data for now during development
        const mockCalls: CallRecord[] = [
          {
            id: '1',
            type: 'outbound',
            duration: 180,
            timestamp: new Date(Date.now() - 7200000).toISOString(),
            status: 'completed',
            recording_url: '/recording1.mp3'
          },
          {
            id: '2',
            type: 'inbound',
            duration: 45,
            timestamp: new Date(Date.now() - 86400000).toISOString(),
            status: 'completed'
          },
          {
            id: '3',
            type: 'missed',
            timestamp: new Date(Date.now() - 172800000).toISOString(),
            status: 'no-answer'
          }
        ];

        setCallHistory(mockCalls);
      }
    } catch (error: any) {
      console.error('Failed to load communication history:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setMessages([]);
      setCallHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const scrollToBottom = React.useCallback(() => {
    // Use setTimeout to ensure DOM is updated and only scroll if element exists
    setTimeout(() => {
      if (messagesContainerRef.current && activeTab === 'messages') {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  }, [activeTab]);

  useEffect(() => {
    setCallNotes(phoneNotes || []);
    loadCommunicationHistory();
  }, [phoneNotes, loadCommunicationHistory]);

  useEffect(() => {
    // Only scroll if we're on the messages tab and there are messages
    if (activeTab === 'messages' && messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, activeTab, scrollToBottom]);

  const sendMessage = async (data: MessageInputs) => {
    let userMessage: SMSMessage | undefined;
    
    try {
      if (!data.message.trim()) return;
      
      if (!leadDetails?.lead_phone) {
        toast.error('No phone number available for this lead');
        return;
      }
      
      setIsLoading(true);
      
      // Add user message to local state immediately
      userMessage = {
        id: String(Date.now()),
        direction: 'sent',
        message: data.message,
        timestamp: new Date().toISOString(),
        status: 'sending'
      };
      
      setMessages(prev => [...prev, userMessage!]);
      reset();
      
      // Send SMS message to lead's phone number
      const response = await axiosInstance.post('/communications/send-sms', {
        to_number: leadDetails.lead_phone,
        message: data.message,
        message_type: 'subscriber'
      });
      
      if (response.data?.success) {
        // Update user message status to delivered
        setMessages(prev => 
          prev.map(msg => 
            msg.id === userMessage!.id 
              ? { ...msg, status: 'delivered' }
              : msg
          )
        );
        
        toast.success('SMS sent successfully!');
        
        // Scroll to bottom after status update
        setTimeout(scrollToBottom, 100);
      } else {
        throw new Error(response.data?.error || 'Failed to send SMS');
      }
      
    } catch (error: any) {
      console.error('Failed to send message:', error);
      
      // Update user message status to failed (only if userMessage was created)
      if (userMessage) {
        setMessages(prev => 
          prev.map(msg => 
            msg.id === userMessage!.id 
              ? { ...msg, status: 'failed' }
              : msg
          )
        );
      }
      
      toast.error(error.response?.data?.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const initiateCall = async () => {
    try {
      if (!leadDetails?.lead_phone) {
        toast.error('No phone number available for this lead');
        return;
      }

      setShowCallModal(true);
      setIsCallActive(false); // Start as connecting
      
      // Get user's assigned phone number first
      const phoneResponse = await axiosInstance.get('/api/communications/my-phone-number');
      
      if (!phoneResponse.data.phone_number) {
        toast.error('You need a business phone number to make calls. Please set one up in Communications settings.');
        setShowCallModal(false);
        return;
      }

      const fromNumber = phoneResponse.data.phone_number;
      const toNumber = leadDetails.lead_phone;
      
      toast.loading('Initiating call...', { id: 'call-toast' });

      // Initiate the Twilio call
              const callResponse = await axiosInstance.post('/communications/initiate-call', {
        from: fromNumber,
        to: toNumber,
        leadId: id
      });

      if (callResponse.data.success) {
        setIsCallActive(true);
        toast.success('Call connected! Check your device.', { id: 'call-toast' });
        
        // Start call duration timer
        const timer = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);

        // Store timer reference for cleanup
        (window as any).callTimer = timer;

        // Add call record to history
        const newCall: CallRecord = {
          id: callResponse.data.callSid || Date.now().toString(),
          type: 'outbound',
          timestamp: new Date().toISOString(),
          status: 'completed',
          duration: 0
        };
        
        setCallHistory(prev => [newCall, ...prev]);
      } else {
        throw new Error(callResponse.data.message || 'Failed to initiate call');
      }
    } catch (error: any) {
      console.error('Call initiation failed:', error);
      toast.error(error.response?.data?.message || 'Failed to initiate call', { id: 'call-toast' });
      setShowCallModal(false);
      setIsCallActive(false);
      setCallDuration(0);
    }
  };

  const endCall = async () => {
    try {
      // Clear the call timer
      if ((window as any).callTimer) {
        clearInterval((window as any).callTimer);
        (window as any).callTimer = null;
      }

      // Update call record with final duration
      if (callDuration > 0) {
        setCallHistory(prev => 
          prev.map(call => 
            call.id === prev[0]?.id 
              ? { ...call, duration: callDuration }
              : call
          )
        );

        // Optionally send call completion to backend
        try {
          await axiosInstance.post('/communications/call-completed', {
            leadId: id,
            duration: callDuration,
            timestamp: new Date().toISOString()
          });
        } catch (error) {
          console.error('Failed to log call completion:', error);
        }
      }

      setIsCallActive(false);
      setShowCallModal(false);
      setCallDuration(0);
      
      toast.success('Call ended');
    } catch (error) {
      console.error('Error ending call:', error);
      setIsCallActive(false);
      setShowCallModal(false);
      setCallDuration(0);
    }
  };

  const addCallNote: SubmitHandler<{ note: string }> = async (data) => {
    try {
      const response = await axiosInstance.post(`/leads/${id}/call-notes`, {
        note: data.note,
      });
      
      const newNote: CallNote = {
        id: Date.now().toString(),
          call_note: data.note,
        created_on: new Date().toISOString(),
      };
      
      setCallNotes(prev => [...prev, newNote]);
      resetNote();
      toast.success('Call note added successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add call note');
    }
  };

  const getMessageStatusIcon = (status: string) => {
    switch (status) {
      case 'sent': return <FontAwesomeIcon icon={faCheck} className="text-muted" />;
      case 'delivered': return <FontAwesomeIcon icon={faCheckDouble} className="text-muted" />;
      case 'read': return <FontAwesomeIcon icon={faCheckDouble} className="text-primary" />;
      case 'failed': return <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" />;
      default: return null;
    }
  };

  const getCallTypeColor = (type: string, status: string) => {
    if (status === 'failed') return 'danger';
    if (type === 'missed') return 'warning';
    if (type === 'outbound') return 'success';
    return 'primary';
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="communication-interface">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="mb-1 fw-semibold" style={{ fontSize: '1.5rem' }}>Communication</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Chat, call, and manage notes with {leadDetails?.lead_name || 'this lead'}
          </p>
        </div>
        
        <div className="d-flex gap-2">
          <Button
            variant="outline-success"
            size="sm"
            onClick={initiateCall}
            className="d-flex align-items-center gap-2"
          >
            <FontAwesomeIcon icon={faPhone} />
            Call
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            className="d-flex align-items-center gap-2"
          >
            <FontAwesomeIcon icon={faVideo} />
            Video
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k as any)}>
        <Nav variant="pills" className="mb-4 communication-tabs">
          <Nav.Item>
            <Nav.Link 
              eventKey="messages" 
              className={classNames('d-flex align-items-center gap-2', {
                'active': activeTab === 'messages'
              })}
            >
              <FontAwesomeIcon icon={faComment} />
              Messages
              <Badge bg="primary" className="ms-1">{messages.length}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              eventKey="calls"
              className={classNames('d-flex align-items-center gap-2', {
                'active': activeTab === 'calls'
              })}
            >
              <FontAwesomeIcon icon={faPhone} />
              Calls
              <Badge bg="secondary" className="ms-1">{callHistory.length}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              eventKey="notes"
              className={classNames('d-flex align-items-center gap-2', {
                'active': activeTab === 'notes'
              })}
            >
              <FontAwesomeIcon icon={faClock} />
              Call Notes
              <Badge bg="info" className="ms-1">{callNotes.length}</Badge>
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Messages Tab */}
          <Tab.Pane eventKey="messages">
            <Card className="communication-card h-100">
              <Card.Body className="p-0 d-flex flex-column" style={{ height: '500px' }}>
                {/* Messages Area */}
                <div 
                  ref={messagesContainerRef}
                  className="flex-grow-1 overflow-auto p-3" 
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  {messages.length === 0 ? (
                    <div className="text-center py-5">
                      <FontAwesomeIcon icon={faComment} size="3x" className="text-muted mb-3" />
                      <h5 className="text-muted">No messages yet</h5>
                      <p className="text-muted">Start a conversation with your lead</p>
                    </div>
                  ) : (
                    <div className="messages-container">
                      {messages.map((message, index) => (
                        <div
                          key={message.id}
                          className={classNames('message-bubble mb-3 d-flex', {
                            'justify-content-end': message.direction === 'sent',
                            'justify-content-start': message.direction === 'received'
                          })}
                        >
                          <div
                            className={classNames('message-content px-3 py-2 rounded-3 position-relative', {
                              'bg-primary text-white': message.direction === 'sent',
                              'bg-white border': message.direction === 'received'
                            })}
                            style={{ 
                              maxWidth: '70%',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}
                          >
                            <p className="mb-1" style={{ fontSize: '0.9rem' }}>
                              {message.message}
                            </p>
                            <div className="d-flex align-items-center justify-content-between">
                              <small 
                                className={classNames({
                                  'text-white-50': message.direction === 'sent',
                                  'text-muted': message.direction === 'received'
                                })}
                                style={{ fontSize: '0.75rem' }}
                              >
                                {getTimeAgo(message.timestamp)}
                              </small>
                              {message.direction === 'sent' && (
                                <span className="ms-2">
                                  {getMessageStatusIcon(message.status)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Message Input */}
                <div className="border-top p-3 bg-white">
                  <Form onSubmit={handleSubmit(sendMessage)}>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Type a message..."
                        {...register('message', { required: true })}
                        className="border-0"
                        style={{ 
                          boxShadow: 'none',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '20px'
                        }}
                      />
                      <Button 
                        variant="primary" 
                        type="submit"
                        disabled={isLoading}
                        className="border-0 rounded-circle ms-2"
                        style={{ width: '40px', height: '40px' }}
                      >
                        <FontAwesomeIcon icon={faPaperPlane} />
                      </Button>
                    </InputGroup>
                  </Form>
                </div>
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Calls Tab */}
          <Tab.Pane eventKey="calls">
            <Card className="communication-card">
              <Card.Body>
                {callHistory.length === 0 ? (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faPhone} size="3x" className="text-muted mb-3" />
                    <h5 className="text-muted">No call history</h5>
                    <p className="text-muted">Your call history will appear here</p>
                  </div>
                ) : (
                  <div className="call-history">
                    {callHistory.map((call) => (
                      <div key={call.id} className="call-record d-flex align-items-center justify-content-between p-3 border-bottom">
                        <div className="d-flex align-items-center gap-3">
                          <div className={`call-icon bg-${getCallTypeColor(call.type, call.status)} text-white rounded-circle d-flex align-items-center justify-content-center`}
                               style={{ width: '40px', height: '40px' }}>
                            <FontAwesomeIcon 
                              icon={call.type === 'missed' ? faPhoneSlash : faPhone} 
                              size="sm"
                            />
                          </div>
                          <div>
                            <div className="fw-semibold">{call.type.charAt(0).toUpperCase() + call.type.slice(1)} Call</div>
                            <small className="text-muted">
                              {call.duration ? formatDuration(call.duration) : 'No answer'} • {getTimeAgo(call.timestamp)}
                            </small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {call.recording_url && (
                            <Button variant="outline-secondary" size="sm">
                              <FontAwesomeIcon icon={faPlay} />
                            </Button>
                          )}
                          <Dropdown>
                            <Dropdown.Toggle variant="link" size="sm" className="text-muted">
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item>Call back</Dropdown.Item>
                              <Dropdown.Item>Add note</Dropdown.Item>
                              {call.recording_url && <Dropdown.Item>Download recording</Dropdown.Item>}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>

          {/* Call Notes Tab */}
          <Tab.Pane eventKey="notes">
            <Card className="communication-card">
              <Card.Body>
                {/* Add Note Form */}
                <Form onSubmit={handleSubmitNote(addCallNote)} className="mb-4">
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Add Call Note</Form.Label>
        <Form.Control
          as="textarea"
                      rows={3}
                      placeholder="Add notes about your call..."
                      {...registerNote('note', { required: true })}
                      className="modern-textarea"
        />
                    {noteErrors.note && (
                      <small className="text-danger">Note is required</small>
        )}
                  </Form.Group>
                  <Button variant="primary" type="submit">
                    Add Note
        </Button>
      </Form>

                {/* Notes List */}
                {callNotes.length === 0 ? (
                  <div className="text-center py-5">
                    <FontAwesomeIcon icon={faClock} size="3x" className="text-muted mb-3" />
                    <h5 className="text-muted">No call notes</h5>
                    <p className="text-muted">Add notes about your calls to keep track</p>
                  </div>
                ) : (
                  <div className="call-notes">
                    {callNotes.map((note, index) => (
        <div
                        key={note.id}
                        className={classNames('call-note p-3 border-bottom', {
                          'border-bottom-0': index === callNotes.length - 1
          })}
        >
                        <p className="mb-2">{note.call_note}</p>
                        <div className="d-flex align-items-center text-muted">
              <FontAwesomeIcon icon={faClock} className="me-2" />
                          <small>{dateFormatter(note.created_on)}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Call Modal */}
      <Modal show={showCallModal} onHide={endCall} centered>
        <Modal.Body className="text-center p-5">
          <div className="call-interface">
            <div className="caller-info mb-4">
              <div className="caller-avatar bg-primary text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                   style={{ width: '80px', height: '80px', fontSize: '1.5rem' }}>
                {leadDetails?.lead_name?.charAt(0) || 'L'}
              </div>
              <h4>{leadDetails?.lead_name || 'Lead'}</h4>
              <p className="text-muted">{leadDetails?.lead_phone || 'Calling...'}</p>
            </div>
            
            <div className="call-status mb-4">
              {isCallActive ? (
                <div className="text-center">
                  <Badge bg="success" className="mb-2 px-3 py-2" style={{ fontSize: '0.9rem' }}>
                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                    Call Active
                  </Badge>
                  <div className="call-timer mt-2" style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                    {formatDuration(callDuration)}
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <Badge bg="warning" className="mb-2 px-3 py-2" style={{ fontSize: '0.9rem' }}>
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    Connecting...
                  </Badge>
                </div>
              )}
            </div>

            <div className="call-controls d-flex justify-content-center gap-3">
              <Button variant="success" className="rounded-circle" style={{ width: '60px', height: '60px' }}>
                <FontAwesomeIcon icon={faMicrophone} />
              </Button>
              <Button variant="danger" onClick={endCall} className="rounded-circle" style={{ width: '60px', height: '60px' }}>
                <FontAwesomeIcon icon={faPhoneSlash} />
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        .communication-interface .communication-tabs .nav-link {
          border-radius: 20px;
          border: 1px solid transparent;
          transition: all 0.2s ease;
        }
        
        .communication-interface .communication-tabs .nav-link:hover {
          background-color: #f8f9fa;
        }
        
        .communication-interface .communication-tabs .nav-link.active {
          background-color: #0d6efd;
          border-color: #0d6efd;
          color: white;
        }
        
        .communication-card {
          border: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          border-radius: 12px;
        }
        
        .message-bubble {
          animation: slideUp 0.3s ease;
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .modern-textarea {
          border-radius: 8px;
          border: 1px solid #e9ecef;
        }
        
        .modern-textarea:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }
      `}</style>
        </div>
  );
};

export default LeadDetailsCommunication;
